import { ChangePathSegment } from "../systems/aws.systems.model";

// Gated diagnostics for the certified-push patch mechanism (generic: every
// attribute of every app, not only one-click). Logs the decision branches —
// skip / primitive-leaf assign / graft of nodes missing locally — so seed gaps
// (e.g. `inverter[]` arriving null from the resolvers and materializing only
// via push) are observable in the wild.
// Toggle via /debug-toggle ([SubPatch]).
const SUB_PATCH_DEBUG = { ENABLED: false };
const spLog = (msg: string) => SUB_PATCH_DEBUG.ENABLED && console.log(`[SubPatch] ${msg}`);
const spWarn = (msg: string) => SUB_PATCH_DEBUG.ENABLED && console.warn(`[SubPatch] ${msg}`);

export const getAttributeIter = (segment: ChangePathSegment, currentAttribute: any) => {
  if (currentAttribute == null) {
    return null;
  }

  if (segment.type === 'attributeList') {
    if (!Array.isArray(currentAttribute)) {
      console.warn('getAttributeIter: currentAttribute is not an array for attributeList segment', {
        segment,
        currentAttribute
      });
      return null;
    }

    const index = currentAttribute.findIndex((item: any) => item[segment.key] === segment.value);
    if (index === -1) {
      console.warn('getAttributeIter: item not found in attributeList', {
        segment,
        currentAttributeLength: currentAttribute.length
      });
      return null;
    }
    return currentAttribute[index];
  }
  return currentAttribute[segment.key];
};

export const deepCloneAndRemoveReadOnly = (obj: any) => {
  const clone = (Array.isArray(obj) ? [] : {}) as any;
  for (const key in obj) {
    const value = obj[key];
    if (typeof value === 'object' && value !== null) {
      clone[key] = deepCloneAndRemoveReadOnly(value);
    } else {
      Object.defineProperty(clone, key, {
        value: value,
        writable: true,
        configurable: true,
        enumerable: true
      });
    }
  }
  return clone;
};

export const removeReadOnly = (obj: any) => {
  const removeReadOnlyRecursive = (currentObj: any) => {
    for (const key in currentObj) {
      const descriptor = Object.getOwnPropertyDescriptor(currentObj, key);
      if (descriptor) {
        if (descriptor.configurable) {
          Object.defineProperty(currentObj, key, {
            writable: true,
            configurable: true,
            enumerable: true
          });
        }

        if (typeof currentObj[key] === 'object' && currentObj[key] !== null) {
          removeReadOnlyRecursive(currentObj[key]);
        }
      }
    }
  };

  const clonedObj = JSON.parse(JSON.stringify(obj));
  removeReadOnlyRecursive(clonedObj);
  return clonedObj;
};

export const processSubscriptionVariation = (segment: ChangePathSegment, localAttribute: any, changedAttribute: any, operationType: string) => {
  if (operationType === 'REMOVE') {
    if (segment.type === 'attributeList') {
      const indexToRemove = localAttribute.findIndex((item: any) => item[segment.key] === segment.value);
      if (indexToRemove !== -1) {
        localAttribute.splice(indexToRemove, 1);
      }
    } else {
      delete localAttribute[segment.key];
    }
  }

  if (operationType === 'INSERT') {
    changedAttribute = getAttributeIter(segment, changedAttribute);
    if (segment.type === 'attributeList') {
      const existingIndex = localAttribute.findIndex((item: any) => item[segment.key] === changedAttribute[segment.key]);
      if (existingIndex !== -1) {
        localAttribute[existingIndex] = changedAttribute;
      } else {
        localAttribute.push(changedAttribute);
      }
    } else {
      localAttribute[segment.key] = changedAttribute;
    }
  }

  if (operationType === 'MODIFY') {
    const localParent = localAttribute;
    const localValue = getAttributeIter(segment, localParent);
    const changedValue = getAttributeIter(segment, changedAttribute);

    if (localParent == null || changedValue == null) {
      /**
       * changedValue == null covers both a failed traversal on the changed side
       * and an explicit null: in both cases keep the local data (a partial
       * subscription payload must never wipe attributes).
       */
      spWarn('MODIFY skipped: parent or changed value is null/undefined ' + JSON.stringify({
        hasLocalParent: localParent != null,
        changedValue,
        segment
      }));
    } else if (typeof changedValue !== 'object') {
      /**
       * Primitive leaf (e.g. a progress/status number on a changesPath ending
       * at the attribute): the assignment must happen on the PARENT. Descending
       * into the value first would leave only a boxed primitive to Object.assign
       * into — a silent no-op that drops every leaf-level change.
       */
      if (segment.type === 'attributeList') {
        spWarn('MODIFY skipped: attributeList segment resolved to a primitive ' + JSON.stringify({ segment }));
      } else {
        localParent[segment.key] = changedValue;
        spLog('MODIFY leaf ' + JSON.stringify({ key: segment.key, value: changedValue }));
      }
    } else if (localValue == null) {
      /**
       * Node missing locally (e.g. an attribute created by this very change,
       * or a seed gap: resolvers returning the node as null and the push
       * materializing it): graft it onto the parent instead of skipping.
       */
      const updatedAttributeClone = deepCloneAndRemoveReadOnly(changedValue);
      if (segment.type === 'attributeList') {
        if (Array.isArray(localParent)) {
          localParent.push(updatedAttributeClone);
          spLog('MODIFY graft (list item missing locally) ' + JSON.stringify({ segment }));
        } else {
          spWarn('MODIFY skipped: attributeList segment on non-array parent ' + JSON.stringify({ segment }));
        }
      } else {
        localParent[segment.key] = updatedAttributeClone;
        spLog('MODIFY graft (node missing locally) ' + JSON.stringify({ segment }));
      }
    } else {
      const updatedAttributeClone = deepCloneAndRemoveReadOnly(changedValue);
      if (Array.isArray(localValue) && Array.isArray(updatedAttributeClone)) {
        // Full replacement: correct ONLY if MODIFY payloads always carry the
        // complete array (L11 open point — watch localLen vs pushLen live:
        // pushLen < localLen on a genuine partial push means data loss here).
        spLog('MODIFY array replace ' + JSON.stringify({ segment, localLen: localValue.length, pushLen: updatedAttributeClone.length }));
        localValue.splice(0, localValue.length, ...updatedAttributeClone);
      } else {
        Object.assign(localValue, updatedAttributeClone);
      }
    }

    localAttribute = localValue;
    changedAttribute = changedValue;
  }

  return { localAttribute, changedAttribute };
};

export const processSegmentIteration = (segment: ChangePathSegment, localAttribute: any, changedAttribute: any): {
  shouldBreak: boolean;
  localAttribute: any;
  changedAttribute: any;
} => {
  changedAttribute = getAttributeIter(segment, changedAttribute);
  /**
   * If the attribute is missing locally, graft the changed subtree onto the
   * local tree so the change is not lost. The graft must MUTATE the object the
   * caller holds (a spread copy would be discarded with the local variable).
   */
  if ([null, undefined].includes(getAttributeIter(segment, localAttribute))) {
    if (changedAttribute != null) {
      const changedClone = deepCloneAndRemoveReadOnly(changedAttribute);
      if (segment.type === 'attributeList') {
        if (Array.isArray(localAttribute)) {
          localAttribute.push(changedClone);
          spLog('graft mid-path (list item missing locally) ' + JSON.stringify({ segment }));
        } else {
          spWarn('graft mid-path skipped: attributeList segment on non-array local ' + JSON.stringify({ segment }));
        }
      } else {
        localAttribute[segment.key] = changedClone;
        spLog('graft mid-path (node missing locally) ' + JSON.stringify({ segment }));
      }
    }

    return { shouldBreak: true, localAttribute, changedAttribute };
  }

  localAttribute = getAttributeIter(segment, localAttribute);
  return { shouldBreak: false, localAttribute, changedAttribute };
};
